/**
 * Deferred Action Registry — Wave 22.13
 *
 * Manages asynchronous action resolution. When a plugin calls
 * `actions.defer()`, the HTTP response returns immediately with
 * `{ deferred: true, actionId }`. The host resolves the action
 * later via `actions.resolve(actionId, result)`.
 *
 * All state transitions are persisted in the invocation log for
 * DevTools inspection.
 */

import { create } from 'zustand';
import type {
  DeferredAction,
  DeferredActionResponse,
  DeferredActionStatus,
  DeferredBody,
  DeferredResolver,
  DeferredRejector,
} from '../types/plugin-interaction';
import { invocationLog } from './interaction-invocation-log';

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

let _nextActionId = 1;

function generateActionId(): string {
  return `deferred-${Date.now()}-${_nextActionId++}`;
}

// ────────────────────────────────────────────────────────
// Zustand Store
// ────────────────────────────────────────────────────────

interface DeferredActionRegistryState {
  /** All deferred actions indexed by actionId. */
  actions: Map<string, DeferredAction>;

  /** Internal promise resolvers for pending actions. */
  _resolvers: Map<string, { resolve: DeferredResolver; reject: DeferredRejector }>;

  /** Create a deferred action, invoke the body, return immediate response. */
  defer<T = any>(
    pluginId: string,
    body: DeferredBody<T>,
    options?: { label?: string; timeoutMs?: number },
  ): DeferredActionResponse;

  /** Resolve a deferred action externally. */
  resolve<T = any>(actionId: string, result: T): void;

  /** Reject a deferred action externally. */
  reject(actionId: string, reason: string): void;

  /** Get all deferred actions (for DevTools). */
  getAll(): DeferredAction[];

  /** Get pending deferred actions. */
  getPending(): DeferredAction[];

  /** Get a specific deferred action. */
  get(actionId: string): DeferredAction | undefined;

  /** Wait for a deferred action to complete. */
  waitFor<T = any>(actionId: string): Promise<T>;

  /** Clear completed/rejected actions (housekeeping). */
  prune(): void;

  /** Phase 3.5: Unregister all deferred actions for a plugin during hot-reload. */
  unregisterPlugin(pluginId: string): void;
}

export const useDeferredActionStore = create<DeferredActionRegistryState>((set, get) => ({
  actions: new Map(),
  _resolvers: new Map(),

  defer<T = any>(
    pluginId: string,
    body: DeferredBody<T>,
    options?: { label?: string; timeoutMs?: number },
  ): DeferredActionResponse {
    const actionId = generateActionId();
    const now = new Date().toISOString();

    const action: DeferredAction<T> = {
      actionId,
      pluginId,
      label: options?.label,
      status: 'pending',
      createdAt: now,
      timeoutMs: options?.timeoutMs,
    };

    // Create the promise that consumers can await
    const resolveAction: DeferredResolver<T> = (value: T) => {
      get().resolve(actionId, value);
    };

    const rejectAction: DeferredRejector = (reason: string) => {
      get().reject(actionId, reason);
    };

    set((state) => {
      const newActions = new Map(state.actions);
      newActions.set(actionId, action);
      return { actions: newActions };
    });

    invocationLog.append({
      kind: 'defer-create',
      pluginId,
      detail: { actionId, label: options?.label },
    });

    // Set up timeout if specified
    if (options?.timeoutMs) {
      setTimeout(() => {
        const current = get().actions.get(actionId);
        if (current && current.status === 'pending') {
          set((state) => {
            const newActions = new Map(state.actions);
            newActions.set(actionId, {
              ...current,
              status: 'timed-out' as DeferredActionStatus,
              completedAt: new Date().toISOString(),
              error: `Action timed out after ${options.timeoutMs}ms`,
            });
            return { actions: newActions };
          });

          // Clean up resolver
          const resolverEntry = get()._resolvers.get(actionId);
          if (resolverEntry) {
            resolverEntry.reject(`Action timed out after ${options.timeoutMs}ms`);
            set((state) => {
              const newResolvers = new Map(state._resolvers);
              newResolvers.delete(actionId);
              return { _resolvers: newResolvers };
            });
          }
        }
      }, options.timeoutMs);
    }

    // Kick off the deferred body (fire-and-forget)
    try {
      const maybePromise = body(resolveAction, rejectAction);
      if (maybePromise && typeof (maybePromise as any).catch === 'function') {
        (maybePromise as Promise<void>).catch((err) => {
          rejectAction(err instanceof Error ? err.message : String(err));
        });
      }
    } catch (err) {
      rejectAction(err instanceof Error ? err.message : String(err));
    }

    return { deferred: true, actionId };
  },

  resolve<T = any>(actionId: string, result: T): void {
    const current = get().actions.get(actionId);
    if (!current || current.status !== 'pending') return;

    const updated: DeferredAction<T> = {
      ...current,
      status: 'resolved',
      result,
      completedAt: new Date().toISOString(),
    };

    set((state) => {
      const newActions = new Map(state.actions);
      newActions.set(actionId, updated);
      return { actions: newActions };
    });

    // Resolve any waitFor() promises
    const resolverEntry = get()._resolvers.get(actionId);
    if (resolverEntry) {
      resolverEntry.resolve(result);
      set((state) => {
        const newResolvers = new Map(state._resolvers);
        newResolvers.delete(actionId);
        return { _resolvers: newResolvers };
      });
    }

    invocationLog.append({
      kind: 'defer-resolve',
      pluginId: current.pluginId,
      detail: { actionId, result },
    });
  },

  reject(actionId: string, reason: string): void {
    const current = get().actions.get(actionId);
    if (!current || current.status !== 'pending') return;

    const updated: DeferredAction = {
      ...current,
      status: 'rejected',
      error: reason,
      completedAt: new Date().toISOString(),
    };

    set((state) => {
      const newActions = new Map(state.actions);
      newActions.set(actionId, updated);
      return { actions: newActions };
    });

    const resolverEntry = get()._resolvers.get(actionId);
    if (resolverEntry) {
      resolverEntry.reject(reason);
      set((state) => {
        const newResolvers = new Map(state._resolvers);
        newResolvers.delete(actionId);
        return { _resolvers: newResolvers };
      });
    }

    invocationLog.append({
      kind: 'defer-reject',
      pluginId: current.pluginId,
      detail: { actionId, reason },
    });
  },

  getAll(): DeferredAction[] {
    return Array.from(get().actions.values());
  },

  getPending(): DeferredAction[] {
    return Array.from(get().actions.values()).filter((a) => a.status === 'pending');
  },

  get(actionId: string): DeferredAction | undefined {
    return get().actions.get(actionId);
  },

  waitFor<T = any>(actionId: string): Promise<T> {
    const current = get().actions.get(actionId);
    if (!current) return Promise.reject(new Error(`Unknown deferred action: ${actionId}`));

    if (current.status === 'resolved') return Promise.resolve(current.result as T);
    if (current.status === 'rejected') return Promise.reject(new Error(current.error));
    if (current.status === 'timed-out') return Promise.reject(new Error(current.error));

    return new Promise<T>((resolve, reject) => {
      set((state) => {
        const newResolvers = new Map(state._resolvers);
        newResolvers.set(actionId, {
          resolve: resolve as DeferredResolver,
          reject: (reason: string) => reject(new Error(reason)),
        });
        return { _resolvers: newResolvers };
      });
    });
  },

  prune(): void {
    set((state) => {
      const newActions = new Map<string, DeferredAction>();
      for (const [id, action] of state.actions) {
        if (action.status === 'pending') {
          newActions.set(id, action);
        }
      }
      return { actions: newActions };
    });
  },

  // Phase 3.5: Unregister all deferred actions for a plugin during hot-reload
  unregisterPlugin(pluginId: string): void {
    set((state) => {
      const newActions = new Map<string, DeferredAction>();
      const newResolvers = new Map(state._resolvers);
      let removed = 0;

      for (const [id, action] of state.actions) {
        if (action.pluginId === pluginId) {
          removed++;
          // Reject any pending actions so waitFor() callers don't hang forever
          if (action.status === 'pending') {
            const resolver = state._resolvers.get(id);
            if (resolver) {
              resolver.reject(`Plugin '${pluginId}' unloaded during hot-reload`);
              newResolvers.delete(id);
            }
          }
        } else {
          newActions.set(id, action);
        }
      }

      if (removed > 0) {
        invocationLog.append({
          kind: 'defer-plugin-unregister' as any,
          pluginId,
          detail: { removedCount: removed },
        });
      }

      return { actions: newActions, _resolvers: newResolvers };
    });
  },
}));
